import React, { useState, useEffect, useCallback, useRef } from 'react';
import { CoinListItem, Holding } from '../../types';
import { useCurrency } from '../../context/CurrencyContext';
import { useCoinList } from '../../hooks/useCoinList';
import { cgGet } from '../../utils/coingecko';
import { CoinSelector } from '../Transactions/CoinSelector';
import { CardHeader } from '../ui/Card';
import { AlertTriangle, X, Plus, Check, ChevronDown, ChevronUp } from 'lucide-react';
import { SplitBuyChart, CHART_RANGES, type ChartRange } from './SplitBuyChart';
import { usePlannerFormat } from './plannerFormat';
import { useStrategy } from '../../context/StrategyContext';
import { usePortfolioContext } from '../../context/PortfolioContext';
import { useSavedSellPlans } from '../../hooks/useSavedPlans';
import { SavedSellPlan } from '../../types/savedPlans';
import { formatDate, formatAmount } from '../../utils/formatters';
import { SellHoldingsPanel } from './SellHoldingsPanel';
import { LivePriceLabel } from './LivePriceLabel';

const TIER_COLORS = [
  { bgClass: 'bg-blue-500',    textClass: 'text-blue-400',    borderClass: 'border-blue-500/30',    hex: '#3b82f6' },
  { bgClass: 'bg-violet-500',  textClass: 'text-violet-400',  borderClass: 'border-violet-500/30',  hex: '#8b5cf6' },
  { bgClass: 'bg-emerald-500', textClass: 'text-emerald-400', borderClass: 'border-emerald-500/30', hex: '#10b981' },
  { bgClass: 'bg-orange-500',  textClass: 'text-orange-400',  borderClass: 'border-orange-500/30',  hex: '#f97316' },
  { bgClass: 'bg-pink-500',    textClass: 'text-pink-400',    borderClass: 'border-pink-500/30',    hex: '#ec4899' },
  { bgClass: 'bg-teal-500',    textClass: 'text-teal-400',    borderClass: 'border-teal-500/30',    hex: '#14b8a6' },
  { bgClass: 'bg-yellow-500',  textClass: 'text-yellow-400',  borderClass: 'border-yellow-500/30',  hex: '#eab308' },
  { bgClass: 'bg-cyan-500',    textClass: 'text-cyan-400',    borderClass: 'border-cyan-500/30',    hex: '#06b6d4' },
  { bgClass: 'bg-rose-500',    textClass: 'text-rose-400',    borderClass: 'border-rose-500/30',    hex: '#f43f5e' },
  { bgClass: 'bg-indigo-500',  textClass: 'text-indigo-400',  borderClass: 'border-indigo-500/30',  hex: '#6366f1' },
];

function tierColor(i: number) {
  return TIER_COLORS[i % TIER_COLORS.length];
}

// Column template for the sell-tiers table:
// dot · sell price · alloc · vs-live · coins sold · proceeds · P&L · remove.
const SELL_TIER_GRID_COLS =
  '14px 116px 68px minmax(72px,0.9fr) minmax(84px,1fr) minmax(100px,1.15fr) minmax(84px,1fr) 22px';

interface SellTier {
  targetPrice: string;
  alloc: string;
}

type SellUnit = 'pct' | 'coins' | 'usd';

const DEFAULT_TIERS: SellTier[] = [
  { targetPrice: '', alloc: '33' },
  { targetPrice: '', alloc: '33' },
  { targetPrice: '', alloc: '34' },
];

interface Props {
  holdings?: Holding[];
  embedded?: { coin: CoinListItem };
  /** Set by the Strategy tab when the user clicks a saved sell plan row. The nonce
   *  changes per click so re-selecting the same plan re-triggers a load. */
  editRequest?: { plan: SavedSellPlan; nonce: number } | null;
  /** Notifies the Strategy tab which plan id is currently loaded (or null). */
  onEditingChange?: (planId: string | null) => void;
  /** Whether this planner's sub-tab is currently visible. Leaving it clears any
   *  in-progress edit session so there's no stale state on return. */
  active?: boolean;
  /** Called with the plan id after a successful save, so the Strategy tab can
   *  scroll to and briefly highlight the saved row. Not called on the Free limit. */
  onPlanSaved?: (planId: string) => void;
}

/**
 * Seed precision for a price field: 6dp under 1, else 2. Shared so the embedded and
 * hosted seeds cannot disagree.
 *
 * The value passed in is ALREADY in the display currency (holding.display.*), which
 * is the position's cost at each purchase's own rate — not today's re-conversion.
 */
function fmtSeed(v: number): string {
  return v.toFixed(v < 1 ? 6 : 2);
}

function isSellPlanEmpty(coinsHeld: string, avgBuyPrice: string, tiers: SellTier[]): boolean {
  if (coinsHeld !== '' || avgBuyPrice !== '') return false;
  return tiers.every(t => !t.targetPrice);
}

export function SplitSellPlanner({ holdings = [], embedded, editRequest, onEditingChange, active, onPlanSaved }: Props = {}) {
  const { fmt, rate, currency } = useCurrency();
  const { fmtCoins, fmtPrice, fmtTotal } = usePlannerFormat();
  const { coins, loading: coinsLoading, error: coinsError } = useCoinList();
  const { sellPlan, updateSellPlan } = useStrategy();
  const { holdings: ctxHoldings } = usePortfolioContext();  // same source as the holdings panel
  const { addPlan: addSavedSellPlan, updatePlan: updateSavedSellPlan } = useSavedSellPlans();

  const [planName,      setPlanName]      = useState('');
  const [savedToast,    setSavedToast]    = useState(false);
  const [savedToastMsg, setSavedToastMsg] = useState('Sell plan saved');
  const [editingPlanId, setEditingPlanId] = useState<string | null>(null);
  const [editingLabel,  setEditingLabel]  = useState('');
  const rootRef = useRef<HTMLDivElement>(null);

  // When embedded, only seed from the saved plan if it's for this coin
  const seedPlan = embedded
    ? (sellPlan?.coinId === embedded.coin.id ? sellPlan : null)
    : sellPlan;

  // For embedded mode, also seed coinsHeld / avgBuyPrice from holdings
  const embeddedHolding = embedded ? holdings.find(h => h.coinId === embedded.coin.id) : undefined;
  const seedCoinsHeld   = seedPlan?.coinsHeld
    ?? (embeddedHolding ? embeddedHolding.amount.toString() : '');
  const seedAvgBuyPrice = seedPlan?.avgBuyPrice
    ?? (embeddedHolding ? fmtSeed(embeddedHolding.display.avgBuyPrice) : '');
  const seedAutoSource: 'portfolio' | 'manual' | null = seedPlan ? null : (embeddedHolding ? 'portfolio' : null);

  const [selectedCoin,      setSelectedCoin]      = useState<CoinListItem | null>(embedded ? embedded.coin : null);
  const [livePriceUsd,      setLivePriceUsd]      = useState<number | null>(null);
  const [livePriceLoading,  setLivePriceLoading]  = useState(false);
  const [coinsHeld,         setCoinsHeld]         = useState(seedCoinsHeld);
  const [avgBuyPrice,       setAvgBuyPrice]       = useState(seedAvgBuyPrice);
  const [coinsHeldSource,   setCoinsHeldSource]   = useState<'portfolio' | 'manual' | null>(seedAutoSource);
  const [avgBuyPriceSource, setAvgBuyPriceSource] = useState<'portfolio' | 'manual' | null>(seedAutoSource);
  const [tiers,             setTiers]             = useState<SellTier[]>(seedPlan?.tiers ?? DEFAULT_TIERS);
  // Price-chart collapse + range owned here (controls move onto the Sell Tiers row
  // when collapsed), matching Split Buy.
  const [chartExpanded,     setChartExpanded]     = useState(false);
  const [chartRange,        setChartRange]        = useState<ChartRange>('90D');

  // ── Sell sizing (how much of the holding to exit) ───────────────────────────
  // `sellFraction` (0–1 of the held position) is the canonical value; the input is
  // shown in the active unit and the slider always represents the fraction. Defaults
  // to selling 100%; a saved plan's sizing is restored via the edit-load effect.
  const [sellUnit,     setSellUnit]     = useState<SellUnit>('pct');
  const [sellFraction, setSellFraction] = useState<number>(1);
  const [sellInputRaw, setSellInputRaw] = useState('');  // populated by the resync effect
  const sellInputFocused = useRef(false);

  // Fetch live price when coin changes
  useEffect(() => {
    if (!selectedCoin) { setLivePriceUsd(null); return; }
    if (selectedCoin.currentPrice > 0) { setLivePriceUsd(selectedCoin.currentPrice); return; }

    let cancelled = false;
    setLivePriceLoading(true);
    cgGet('/simple/price', { params: { ids: selectedCoin.id, vs_currencies: 'usd' }, timeout: 8_000 })
      .then(({ data }) => {
        if (cancelled) return;
        const price = (data as Record<string, { usd: number }>)[selectedCoin.id]?.usd ?? 0;
        setLivePriceUsd(price > 0 ? price : null);
      })
      .catch(() => { if (!cancelled) setLivePriceUsd(null); })
      .finally(() => { if (!cancelled) setLivePriceLoading(false); });
    return () => { cancelled = true; };
  }, [selectedCoin]);

  const handleCoinSelect = useCallback((coin: CoinListItem) => {
    setSelectedCoin(coin);
    setEditingPlanId(null);
    setEditingLabel('');
    // Re-size against the new coin's holdings (default to 100%).
    setSellUnit('pct');
    setSellFraction(1);
    const holding = holdings.find(h => h.coinId === coin.id);
    if (holding) {
      setCoinsHeld(holding.amount.toString());
      const priceInCurrency = holding.display.avgBuyPrice;
      setAvgBuyPrice(priceInCurrency.toFixed(priceInCurrency < 1 ? 6 : 2));
      setCoinsHeldSource('portfolio');
      setAvgBuyPriceSource('portfolio');
    } else {
      setCoinsHeld('');
      setAvgBuyPrice('');
      setCoinsHeldSource(null);
      setAvgBuyPriceSource(null);
    }
  }, [holdings, rate]);
  const updateTier = useCallback((index: number, field: keyof SellTier, value: string) => {
    setTiers(prev => prev.map((t, i) => i === index ? { ...t, [field]: value } : t));
  }, []);
  const addTier = useCallback(() => {
    setTiers(prev => [...prev, { targetPrice: '', alloc: '' }]);
  }, []);
  const removeTier = useCallback((index: number) => {
    setTiers(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Restore selected coin from context once coins list loads (non-embedded only)
  const restoredCoinRef = useRef(false);
  useEffect(() => {
    if (embedded || restoredCoinRef.current || !coins.length || !sellPlan?.coinId) return;
    restoredCoinRef.current = true;
    const coin = coins.find(c => c.id === sellPlan.coinId) ?? null;
    if (coin) setSelectedCoin(coin);
  }, [coins]); // eslint-disable-line react-hooks/exhaustive-deps

  // When embedded route changes, re-init local state for the new coin
  useEffect(() => {
    if (!embedded) return;
    if (selectedCoin?.id === embedded.coin.id) return;
    setSelectedCoin(embedded.coin);
    setSellUnit('pct');
    setSellFraction(1);
    if (sellPlan?.coinId === embedded.coin.id) {
      setCoinsHeld(sellPlan.coinsHeld);
      setAvgBuyPrice(sellPlan.avgBuyPrice);
      setTiers(sellPlan.tiers);
      setCoinsHeldSource(null);
      setAvgBuyPriceSource(null);
    } else {
      const h = holdings.find(x => x.coinId === embedded.coin.id);
      if (h) {
        setCoinsHeld(h.amount.toString());
        const priceInCurrency = h.display.avgBuyPrice;
        setAvgBuyPrice(priceInCurrency.toFixed(priceInCurrency < 1 ? 6 : 2));
        setCoinsHeldSource('portfolio');
        setAvgBuyPriceSource('portfolio');
      } else {
        setCoinsHeld('');
        setAvgBuyPrice('');
        setCoinsHeldSource(null);
        setAvgBuyPriceSource(null);
      }
      setTiers(DEFAULT_TIERS);
    }
  }, [embedded?.coin.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Default "Coins Held" to the user's actual holdings of the selected coin — using
  // the SAME PortfolioContext figure the "Your Holdings" panel shows. Runs once per
  // coin (ref-guarded) and only when the field is empty and not user-edited, so it
  // covers selection paths that don't fill it (e.g. coin restored from the draft) and
  // never clobbers a manual override or a loaded saved plan. Switching coins re-fills.
  const autoCoinRef = useRef<string | null>(null);
  useEffect(() => {
    const id = selectedCoin?.id ?? null;
    if (autoCoinRef.current === id) return;
    autoCoinRef.current = id;
    if (!id) return;
    if (coinsHeldSource === 'manual') return;
    if (coinsHeld.trim() !== '') return;  // already populated (handleCoinSelect / plan / draft)
    const h = ctxHoldings.find(x => x.coinId === id) ?? holdings.find(x => x.coinId === id);
    if (h) {
      setCoinsHeld(String(h.amount));
      setCoinsHeldSource('portfolio');
    } else {
      setCoinsHeld('0');
      setCoinsHeldSource(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCoin?.id, ctxHoldings]);

  // Persist plan to context whenever state changes (skip auto-overwrite of other coin's plan)
  useEffect(() => {
    if (!selectedCoin) return;
    if (isSellPlanEmpty(coinsHeld, avgBuyPrice, tiers) && sellPlan && sellPlan.coinId !== selectedCoin.id) return;
    updateSellPlan({
      coinId:      selectedCoin.id,
      coinName:    selectedCoin.name,
      coinSymbol:  selectedCoin.symbol,
      coinImage:   selectedCoin.image ?? '',
      coinsHeld,
      avgBuyPrice,
      tiers,
      currencyCode: currency.code,
    });
  }, [selectedCoin?.id, coinsHeld, avgBuyPrice, tiers, currency.code]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Parsed values ──────────────────────────────────────────────────────────
  const coinsHeldNum      = parseFloat(coinsHeld) || 0;
  const avgBuyPriceNum    = parseFloat(avgBuyPrice) || 0;
  const tierAllocs        = tiers.map(t => parseFloat(t.alloc) || 0);
  const tierPrices        = tiers.map(t => parseFloat(t.targetPrice) || 0);
  const allocSum          = tierAllocs.reduce((a, b) => a + b, 0);
  const allocValid        = Math.abs(allocSum - 100) < 0.01;
  const hasAvgBuyPrice    = avgBuyPriceNum > 0;

  // ── Sell sizing ─────────────────────────────────────────────────────────────
  // Current market price (USD) for valuing the position: prefer the live price,
  // fall back to the portfolio holding / coin's known price.
  const holdingForCoin = selectedCoin ? holdings.find(h => h.coinId === selectedCoin.id) : undefined;
  const priceUsd = livePriceUsd ?? holdingForCoin?.currentPrice ?? selectedCoin?.currentPrice ?? 0;

  const sellQtyNum    = Math.min(coinsHeldNum, Math.max(0, sellFraction * coinsHeldNum));
  const holdQtyNum    = Math.max(0, coinsHeldNum - sellQtyNum);
  const totalValueUsd = coinsHeldNum * priceUsd;  // USD
  const sellValueUsd  = sellQtyNum * priceUsd;
  const holdValueUsd  = holdQtyNum * priceUsd;

  // Convert the canonical fraction to/from the value shown in a given unit.
  const trimNum = (n: number, maxDec: number) => (isFinite(n) ? String(Number(n.toFixed(maxDec))) : '');
  const fracToInput = (frac: number, unit: SellUnit): string => {
    if (unit === 'pct')   return trimNum(frac * 100, 2);
    if (unit === 'coins') return trimNum(frac * coinsHeldNum, 8);
    return trimNum(frac * coinsHeldNum * priceUsd * rate, 2);  // usd, display currency
  };
  const inputToFrac = (raw: string, unit: SellUnit): number => {
    const v = parseFloat(raw);
    if (!isFinite(v) || v <= 0) return 0;
    let f: number;
    if (unit === 'pct')        f = v / 100;
    else if (unit === 'coins') f = coinsHeldNum > 0 ? v / coinsHeldNum : 0;
    else { const denom = coinsHeldNum * priceUsd * rate; f = denom > 0 ? v / denom : 0; }
    return Math.min(1, Math.max(0, f));
  };

  // ── Per-tier calcs (all values in selected currency) — allocate the SELL qty ─
  const totalInitialCost  = sellQtyNum * avgBuyPriceNum;  // cost basis of the sold portion
  const tierResults = tiers.map((_, i) => {
    const coinsSold      = (tierAllocs[i] / 100) * sellQtyNum;
    const proceeds       = coinsSold * tierPrices[i];
    const remainingCoins = sellQtyNum - coinsSold;
    const remainingValue = remainingCoins * tierPrices[i];
    const costBasis      = coinsSold * avgBuyPriceNum;
    const pnlDollar      = proceeds - costBasis;
    const pnlPercent     = costBasis > 0 ? (pnlDollar / costBasis) * 100 : 0;
    const isProfitable   = avgBuyPriceNum > 0 && tierPrices[i] > avgBuyPriceNum;
    const isLoss         = avgBuyPriceNum > 0 && tierPrices[i] > 0 && tierPrices[i] < avgBuyPriceNum;
    // how many coins to sell at this price to recover the sold portion's cost
    const breakEvenUnits = totalInitialCost > 0 && tierPrices[i] > 0
      ? totalInitialCost / tierPrices[i]
      : 0;
    return { coinsSold, proceeds, remainingCoins, remainingValue, costBasis, pnlDollar, pnlPercent, isProfitable, isLoss, breakEvenUnits };
  });

  // ── Aggregate calcs ────────────────────────────────────────────────────────
  const totalProceeds   = tierResults.reduce((s, t) => s + t.proceeds, 0);
  const totalCoinsSold  = tierResults.reduce((s, t) => s + t.coinsSold, 0);
  const totalCostBasis  = totalCoinsSold * avgBuyPriceNum;
  const totalPnl        = totalProceeds - totalCostBasis;
  const totalPnlPct     = totalCostBasis > 0 ? (totalPnl / totalCostBasis) * 100 : 0;
  const blendedExit     = totalCoinsSold > 0 ? totalProceeds / totalCoinsSold : 0;
  const canShowSummary  = allocValid && sellQtyNum > 0 && totalProceeds > 0;

  // Note when expected proceeds differ from the dollar-sizing (tiers off current price).
  const sellValueDisplay   = sellValueUsd * rate;
  const proceedsDiff       = totalProceeds - sellValueDisplay;
  const showProceedsNote   = canShowSummary && sellValueDisplay > 0
    && Math.abs(proceedsDiff) / sellValueDisplay > 0.01;

  // ── Chart tier overlay lines ───────────────────────────────────────────────
  const tierLines = tiers.map((_, i) => ({
    price:     tierPrices[i],
    tierIndex: i,
    color:     tierColor(i).hex,
  }));

  // Keep the sell-amount input in sync with the canonical fraction when the unit,
  // held quantity or price changes — but never while the user is actively typing.
  useEffect(() => {
    if (sellInputFocused.current) return;
    setSellInputRaw(fracToInput(sellFraction, sellUnit));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellFraction, sellUnit, coinsHeldNum, priceUsd, rate]);

  const handleSellUnitChange = (unit: SellUnit) => {
    setSellUnit(unit);
    setSellInputRaw(fracToInput(sellFraction, unit));
  };
  const handleSellInputChange = (raw: string) => {
    setSellInputRaw(raw);
    setSellFraction(inputToFrac(raw, sellUnit));
  };
  const handleSellSlider = (pct: number) => {
    const f = Math.min(1, Math.max(0, pct / 100));
    setSellFraction(f);
    setSellInputRaw(fracToInput(f, sellUnit));
  };

  // Reset the planner to a fresh, blank state and exit any edit session.
  const resetPlanner = useCallback(() => {
    setEditingPlanId(null);
    setEditingLabel('');
    setPlanName('');
    setCoinsHeld('');
    setAvgBuyPrice('');
    setCoinsHeldSource(null);
    setAvgBuyPriceSource(null);
    setTiers(DEFAULT_TIERS);
    setSellUnit('pct');
    setSellFraction(1);
    if (!embedded) setSelectedCoin(null);
  }, [embedded]);

  // ── Save plan (frozen snapshot — never affects holdings / cost basis / P&L) ──
  const canSave = canShowSummary;
  const handleSavePlan = useCallback(() => {
    if (!canSave || !selectedCoin) return;
    const snapshot = {
      coinId:     selectedCoin.id,
      coinName:   selectedCoin.name,
      coinTicker: selectedCoin.symbol.toUpperCase(),
      coinImage:  selectedCoin.image ?? '',
      coinsHeld:  coinsHeldNum,
      avgBuyPrice: avgBuyPriceNum,
      tiers: tiers.map((_, i) => ({
        price:             tierPrices[i],
        allocationPercent: tierAllocs[i],
        allocationAmount:  tierResults[i].proceeds,
      })),
      tierCount:        tiers.length,
      expectedProceeds: totalProceeds,
      currency:         currency.code,
      planName:         planName.trim() || `Saved ${formatDate(new Date().toISOString())}`,
      // Sell sizing — capture what the user planned to sell (not the whole holding)
      sellQuantity:     sellQtyNum,
      sellPercent:      sellFraction * 100,
      sellUnit,
      sellValue:        parseFloat(sellInputRaw) || 0,
    };
    let savedId: string;
    if (editingPlanId) {
      updateSavedSellPlan(editingPlanId, snapshot);
      setSavedToastMsg('Plan updated');
      savedId = editingPlanId;
    } else {
      savedId = addSavedSellPlan(snapshot).id;
      setSavedToastMsg('Sell plan saved');
    }
    setSavedToast(true);
    resetPlanner();   // ready for a fresh entry
    onPlanSaved?.(savedId);   // scroll up to + highlight the saved row
  }, [canSave, selectedCoin, coinsHeldNum, avgBuyPriceNum, tiers, tierPrices, tierAllocs, tierResults, totalProceeds, currency.code, planName, editingPlanId, sellQtyNum, sellFraction, sellUnit, sellInputRaw, addSavedSellPlan, updateSavedSellPlan, resetPlanner, onPlanSaved]);

  useEffect(() => {
    if (!savedToast) return;
    const t = setTimeout(() => setSavedToast(false), 4000);
    return () => clearTimeout(t);
  }, [savedToast]);

  // Surface the active edit target to the Strategy tab (for the row highlight).
  useEffect(() => { onEditingChange?.(editingPlanId); }, [editingPlanId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Leaving this sub-tab while editing clears the session so nothing is stale.
  const prevActive = useRef(active);
  useEffect(() => {
    if (prevActive.current && !active && editingPlanId) resetPlanner();
    prevActive.current = active;
  }, [active, editingPlanId, resetPlanner]);

  // Load a saved sell plan into the planner when the Strategy tab requests an edit.
  const lastEditNonce = useRef(0);
  useEffect(() => {
    if (!editRequest || editRequest.nonce === lastEditNonce.current) return;
    lastEditNonce.current = editRequest.nonce;
    const plan = editRequest.plan;
    setSelectedCoin({
      id: plan.coinId,
      symbol: plan.coinTicker,
      name: plan.coinName,
      image: plan.coinImage ?? '',
      currentPrice: 0, marketCapRank: 0, marketCap: 0, volume24h: 0, high24h: 0, low24h: 0,
    });
    setCoinsHeld(plan.coinsHeld ? String(plan.coinsHeld) : '');
    setAvgBuyPrice(plan.avgBuyPrice ? String(plan.avgBuyPrice) : '');
    setCoinsHeldSource(null);
    setAvgBuyPriceSource(null);
    // Restore the sell sizing (pre-sizing plans default to 100% of the holding).
    setSellUnit(plan.sellUnit ?? 'pct');
    setSellFraction(Math.min(1, Math.max(0, (plan.sellPercent ?? 100) / 100)));
    setTiers(plan.tiers.map(t => ({ targetPrice: String(t.price), alloc: String(t.allocationPercent) })));
    setPlanName(plan.planName);
    setEditingPlanId(plan.id);
    setEditingLabel(plan.planName || plan.coinName);
    requestAnimationFrame(() => {
      rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [editRequest]);

  const inputClass = 'w-full bg-slate-800 border border-slate-700 rounded-xl px-4 py-2.5 text-sm text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-slate-500 transition-colors';
  const labelClass = 'block text-xs font-medium text-slate-400 mb-1.5 uppercase tracking-wider';
  // Compact input used inside the dense tiers table.
  const tierInputClass = 'w-full bg-slate-800 border border-slate-700 rounded px-2 py-1 text-xs text-slate-100 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-white/20 focus:border-slate-500 transition-colors';
  const dash       = <span className="text-slate-600">—</span>;

  // Placeholder that hints the expected price magnitude for the selected asset,
  // using the live price in the display currency (mirrors Split Buy's inputs).
  const livePriceDisplay = livePriceUsd != null ? livePriceUsd * rate : null;
  const pricePlaceholder = livePriceDisplay && livePriceDisplay > 0
    ? `e.g. ${
        livePriceDisplay >= 1000 ? Math.round(livePriceDisplay).toLocaleString('en-US')
        : livePriceDisplay >= 1  ? livePriceDisplay.toLocaleString('en-US', { maximumFractionDigits: 2 })
        :                          livePriceDisplay.toLocaleString('en-US', { maximumFractionDigits: 6 })
      }`
    : '0';

  // Range buttons + collapse chevron; rendered on the "Price chart" row when
  // expanded, or on the Sell Tiers row when collapsed (only one mounts at a time).
  const hasCoin = !!selectedCoin;
  const chartControls = (
    <div className="flex items-center gap-1">
      {hasCoin && CHART_RANGES.map(r => (
        <button
          key={r}
          type="button"
          onClick={() => setChartRange(r)}
          className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
            chartRange === r
              ? 'bg-slate-700 text-slate-100 border border-slate-600'
              : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/60'
          }`}
        >
          {r}
        </button>
      ))}
      <button
        type="button"
        onClick={() => setChartExpanded(v => !v)}
        className="ml-1 p-1 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/60 transition-colors"
        aria-label={chartExpanded ? 'Collapse chart' : 'Expand chart'}
        aria-expanded={chartExpanded}
      >
        {chartExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
    </div>
  );

  const coinsHeldField = (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Coins Held</span>
        {coinsHeldSource === 'portfolio' && (
          <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            From portfolio
          </span>
        )}
      </div>
      <input
        type="number"
        min="0"
        value={coinsHeld}
        onChange={e => { setCoinsHeld(e.target.value); setCoinsHeldSource('manual'); }}
        placeholder="e.g. 0.5"
        className={inputClass}
      />
    </div>
  );

  const avgBuyField = (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Avg Buy Price ({currency.code})</span>
        {avgBuyPriceSource === 'portfolio' && (
          <span className="text-xs font-medium px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
            From portfolio
          </span>
        )}
      </div>
      <input
        type="number"
        min="0"
        value={avgBuyPrice}
        onChange={e => { setAvgBuyPrice(e.target.value); setAvgBuyPriceSource('manual'); }}
        placeholder="e.g. 65000"
        className={inputClass}
      />
      {hasAvgBuyPrice && coinsHeldNum > 0 && (
        <p className="mt-1.5 text-xs text-slate-500">
          Cost basis: <span className="text-slate-400 font-mono">{fmtTotal(totalInitialCost)}</span>
        </p>
      )}
    </div>
  );

  const inner = (
    <>
      {/* ── Top inputs ─────────────────────────────────────────────────────── */}
      {embedded ? (
        <>
          {selectedCoin && (livePriceUsd != null || livePriceLoading) && (
            <div className="h-4 flex items-center">
              <LivePriceLabel selectedCoin={selectedCoin} livePriceUsd={livePriceUsd} livePriceLoading={livePriceLoading} />
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {coinsHeldField}
            {avgBuyField}
          </div>
        </>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <div className="flex items-center gap-2 mb-1.5 min-h-[16px]">
              <label className="block text-xs font-medium text-slate-400 uppercase tracking-wider">Asset</label>
              {selectedCoin && (
                livePriceLoading ? (
                  <span className="text-[11px] text-slate-500">live …</span>
                ) : livePriceUsd != null ? (
                  <span className="text-[11px] text-slate-500">
                    live <span className="text-slate-300 font-mono">{fmtPrice(livePriceUsd * rate)}</span>
                  </span>
                ) : null
              )}
            </div>
            <CoinSelector
              coins={coins}
              selectedId={selectedCoin?.id ?? ''}
              onSelect={handleCoinSelect}
              loading={coinsLoading}
              error={coinsError}
            />
          </div>
          {coinsHeldField}
          {avgBuyField}
        </div>
      )}

      {/* ── Your holdings (informational — does not constrain the sell tiers) ── */}
      {selectedCoin && (
        <SellHoldingsPanel coinId={selectedCoin.id} symbol={selectedCoin.symbol} />
      )}

      {/* ── How much to sell (sizes the exit; tiers then stage this amount) ──── */}
      {selectedCoin && (
        <div
          style={{
            background: 'rgb(var(--overlay) / 0.02)',
            border: '0.5px solid rgb(var(--overlay) / 0.05)',
            borderRadius: 10,
            padding: 14,
          }}
        >
          <p className="uppercase font-semibold mb-2" style={{ fontSize: 10, letterSpacing: '0.1em', color: '#b89968' }}>
            How much to sell
          </p>

          {coinsHeldNum > 0 ? (
            <>
              {/* Holdings reference */}
              <p className="text-xs text-slate-500 mb-3">
                <span className="num text-slate-300">{formatAmount(coinsHeldNum)} {selectedCoin.symbol}</span>
                {priceUsd > 0 && <> · ≈ {fmt(totalValueUsd)} held · Live: {fmt(priceUsd)}</>}
              </p>

              {/* Unit toggle */}
              <div className="inline-flex rounded-lg overflow-hidden border border-slate-700 mb-3">
                {(['pct', 'coins', 'usd'] as SellUnit[]).map((u) => {
                  const activeU = sellUnit === u;
                  const lbl = u === 'pct' ? '%' : u === 'coins' ? 'Coins' : '$ value';
                  return (
                    <button
                      key={u}
                      type="button"
                      onClick={() => handleSellUnitChange(u)}
                      className="px-3 py-1.5 text-xs font-medium transition-colors"
                      style={activeU
                        ? { background: '#d4b483', color: '#0c0e12' }
                        : { background: 'transparent', color: 'var(--color-sub)' }}
                    >
                      {lbl}
                    </button>
                  );
                })}
              </div>

              {/* Amount input + slider */}
              <div className="relative mb-2">
                <input
                  type="number"
                  min="0"
                  value={sellInputRaw}
                  onFocus={() => { sellInputFocused.current = true; }}
                  onBlur={() => { sellInputFocused.current = false; setSellInputRaw(fracToInput(sellFraction, sellUnit)); }}
                  onChange={(e) => handleSellInputChange(e.target.value)}
                  className={inputClass + ' pr-16'}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500 pointer-events-none">
                  {sellUnit === 'pct' ? '%' : sellUnit === 'coins' ? selectedCoin.symbol : currency.code}
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="0.1"
                value={sellFraction * 100}
                onChange={(e) => handleSellSlider(parseFloat(e.target.value))}
                className="w-full"
                style={{ accentColor: '#d4b483' }}
              />
              <p className="text-xs text-slate-500 mt-1">{(sellFraction * 100).toFixed(1)}% of holdings</p>

              {/* Selling / Holding breakdown */}
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div className="rounded-lg p-3" style={{ background: 'rgba(196,97,94,0.08)', border: '0.5px solid rgba(196,97,94,0.22)' }}>
                  <p className="uppercase mb-1" style={{ fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-negative)' }}>Selling</p>
                  <p className="num text-sm font-medium" style={{ color: 'rgb(var(--sl-100))' }}>{formatAmount(sellQtyNum)} {selectedCoin.symbol}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--color-sub)' }}>≈ {fmt(sellValueUsd)} at current price</p>
                </div>
                <div className="rounded-lg p-3" style={{ background: 'rgb(var(--overlay) / 0.03)', border: '0.5px solid rgb(var(--overlay) / 0.07)' }}>
                  <p className="uppercase mb-1" style={{ fontSize: 9, letterSpacing: '0.08em', color: 'var(--color-sub)' }}>Holding</p>
                  <p className="num text-sm font-medium" style={{ color: 'rgb(var(--sl-100))' }}>{formatAmount(holdQtyNum)} {selectedCoin.symbol}</p>
                  <p className="text-xs mt-0.5" style={{ color: 'var(--color-sub)' }}>≈ {fmt(holdValueUsd)} kept</p>
                </div>
              </div>
            </>
          ) : (
            <p className="text-xs text-slate-500">
              You don&apos;t currently hold any {selectedCoin.symbol} to sell. Enter a position in “Coins Held” above to plan hypothetically.
            </p>
          )}
        </div>
      )}

      {/* ── Price chart — collapsed by default; controls move to the Sell Tiers row ── */}
      {chartExpanded && (
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Price chart</span>
            <div className="ml-auto">{chartControls}</div>
          </div>
          <SplitBuyChart
            coinId={selectedCoin?.id ?? ''}
            symbol={selectedCoin?.symbol ?? ''}
            tierLines={tierLines}
            range={chartRange}
          />
        </div>
      )}

      {/* ── Sell tiers table ────────────────────────────────────────────────── */}
      <div>
        <div className="flex items-center justify-between gap-2 mb-2">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Sell Tiers</span>
          <div className="flex items-center gap-2">
            {allocSum > 0 && (
              allocValid
                ? <span className="text-xs text-emerald-400">✓ 100% allocated</span>
                : <span className="text-xs text-amber-400 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" />{allocSum.toFixed(1)}% / 100%</span>
            )}
            {!chartExpanded && chartControls}
          </div>
        </div>

        <div className="rounded-xl border border-slate-700 overflow-hidden">
          {/* Column headers (desktop) */}
          <div className="hidden sm:grid items-center gap-2 px-3 py-2 border-b border-slate-700 bg-slate-800/40" style={{ gridTemplateColumns: SELL_TIER_GRID_COLS }}>
            <span />
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Price ({currency.code})</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500">Alloc %</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">vs Live</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Coins</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">Proceeds</span>
            <span className="text-[10px] uppercase tracking-wider text-slate-500 text-right">P&amp;L</span>
            <span />
          </div>

          {/* Tier rows — desktop grid + mobile stacked two-line variant */}
          {tiers.map((tier, i) => {
            const color        = tierColor(i);
            const result       = tierResults[i];
            const sellPriceNum = tierPrices[i];
            const allocNum     = tierAllocs[i];
            const hasPrice     = sellPriceNum > 0;
            const hasCoins     = sellQtyNum > 0;
            const hasAlloc     = allocNum > 0;
            const sym          = selectedCoin?.symbol ?? '';

            const coinsSold = hasCoins && hasAlloc ? result.coinsSold : null;
            const proceeds  = hasCoins && hasAlloc && hasPrice ? result.proceeds : null;
            const pnlDollar = (coinsSold != null && hasAvgBuyPrice && hasPrice) ? result.pnlDollar : null;

            // vs live — INVERTED sign for selling: a target ABOVE live is the good
            // case (green); below live is amber. Display-only, never persisted.
            const liveDisplay = livePriceUsd != null ? livePriceUsd * rate : null;
            const vsLivePct = liveDisplay != null && liveDisplay > 0 && hasPrice
              ? (sellPriceNum / liveDisplay - 1) * 100
              : null;
            const vsLiveStr = vsLivePct != null ? `${vsLivePct > 0 ? '+' : ''}${vsLivePct.toFixed(1)}%` : '—';
            const vsLiveColor = vsLivePct == null ? 'text-slate-600'
              : vsLivePct > 0 ? 'text-emerald-400' : vsLivePct < 0 ? 'text-amber-400' : 'text-slate-400';

            const coinsStr    = coinsSold != null ? fmtCoins(coinsSold) : '—';
            const proceedsStr = proceeds != null ? fmtTotal(proceeds) : '—';
            const pnlStr      = pnlDollar != null ? `${pnlDollar >= 0 ? '+' : ''}${fmtTotal(pnlDollar)}` : '—';
            const pnlColor    = pnlDollar == null ? 'text-slate-600' : pnlDollar >= 0 ? 'text-emerald-400' : 'text-red-400';

            const sellInput = (
              <input
                type="number" min="0"
                value={tier.targetPrice}
                onChange={e => updateTier(i, 'targetPrice', e.target.value)}
                placeholder={pricePlaceholder}
                aria-label={`Tier ${i + 1} sell price`}
                className={tierInputClass}
              />
            );
            const allocInput = (
              <input
                type="number" min="0" max="100"
                value={tier.alloc}
                onChange={e => updateTier(i, 'alloc', e.target.value)}
                placeholder="0"
                aria-label={`Tier ${i + 1} allocation percent`}
                className={tierInputClass}
              />
            );
            const removeBtn = i > 0 ? (
              <button
                type="button"
                onClick={() => removeTier(i)}
                className="p-0.5 rounded text-slate-500 hover:text-slate-200 hover:bg-slate-700/60 transition-colors justify-self-end"
                aria-label={`Remove tier ${i + 1}`}
              >
                <X className="w-3.5 h-3.5" />
              </button>
            ) : null;

            return (
              <React.Fragment key={i}>
                {/* Desktop grid row */}
                <div className="hidden sm:grid items-center gap-2 px-3 border-b border-slate-700/50" style={{ gridTemplateColumns: SELL_TIER_GRID_COLS, minHeight: 34 }}>
                  <span className={`w-2 h-2 rounded-full ${color.bgClass} flex-shrink-0`} />
                  {sellInput}
                  {allocInput}
                  <span className={`text-xs tabular-nums truncate text-right ${vsLiveColor}`}>{vsLiveStr}</span>
                  <span className={`text-xs tabular-nums truncate text-right ${coinsSold != null ? color.textClass : ''}`}>
                    {coinsSold != null
                      ? <>{coinsStr} <span className="text-[11px] font-normal text-slate-500">{sym}</span></>
                      : dash}
                  </span>
                  <span className="text-xs text-slate-200 tabular-nums truncate text-right">
                    {proceeds != null ? proceedsStr : dash}
                  </span>
                  <span className={`text-xs tabular-nums truncate text-right ${pnlColor}`}>{pnlStr}</span>
                  {removeBtn ?? <span />}
                </div>

                {/* Mobile stacked row */}
                <div className="sm:hidden px-3 py-2.5 border-b border-slate-700/50">
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${color.bgClass} flex-shrink-0`} />
                    <div className="flex-1">{sellInput}</div>
                    <div className="w-16 flex-shrink-0">{allocInput}</div>
                    {removeBtn}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1.5 pl-4 text-[11px] tabular-nums text-slate-500">
                    <span>vs live <span className={vsLiveColor}>{vsLiveStr}</span></span>
                    <span>coins <span className="text-slate-300">{coinsSold != null ? `${coinsStr} ${sym}` : '—'}</span></span>
                    <span>proceeds <span className="text-slate-300">{proceedsStr}</span></span>
                    <span>p&amp;l <span className={pnlColor}>{pnlStr}</span></span>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Add tier — compact last row inside the same border */}
          <button
            type="button"
            onClick={addTier}
            className="w-full flex items-center justify-center gap-1.5 py-2 text-xs font-medium text-slate-400 hover:text-slate-100 hover:bg-slate-800/60 transition-colors"
          >
            <Plus className="w-3.5 h-3.5" />
            Add tier
          </button>
        </div>

        {/* ── Sell allocation — thin bar directly under the table ──────────── */}
        {allocSum > 0 && (
          <div className="mt-2 h-[5px] rounded-full overflow-hidden flex bg-slate-800">
            {tiers.map((tier, i) => {
              const pct        = parseFloat(tier.alloc) || 0;
              const displayPct = allocSum > 0 ? (pct / allocSum) * 100 : 0;
              if (displayPct <= 0) return null;
              return (
                <div
                  key={i}
                  className={`${tierColor(i).bgClass} transition-all duration-300`}
                  style={{ width: `${displayPct}%` }}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* ── Validation warning ─────────────────────────────────────────────── */}
      {!allocValid && sellQtyNum > 0 && allocSum > 0 && (
        <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-500/5 border border-amber-500/20">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0" />
          <p className="text-sm text-amber-300">
            Summary hidden until allocations sum to exactly 100% (currently {allocSum.toFixed(1)}%).
          </p>
        </div>
      )}

      {/* ── Summary + profit — one horizontal strip ─────────────────────────── */}
      {canShowSummary && (
        <div className="rounded-xl bg-slate-900/60 border border-slate-700 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-4">
            {/* Left — coins sold / avg exit / total proceeds */}
            <div className="grid grid-cols-3 gap-4 lg:flex-1">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Coins sold</p>
                <p className="text-sm font-semibold text-slate-100 tabular-nums">
                  {fmtCoins(totalCoinsSold)} <span className="text-[11px] font-normal text-slate-500">{selectedCoin?.symbol}</span>
                </p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Avg exit price</p>
                <p className="text-sm font-semibold text-slate-100 tabular-nums">{fmtPrice(blendedExit)}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Total proceeds</p>
                <p className="text-sm font-semibold text-slate-100 tabular-nums">{fmtTotal(totalProceeds)}</p>
              </div>
            </div>

            {/* Right — P&L + return (once avg buy price is known) */}
            {hasAvgBuyPrice && (
              <div className="flex flex-wrap items-end gap-4 lg:border-l lg:border-slate-700 lg:pl-4">
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Total P&amp;L</p>
                  <p className={`text-sm font-semibold tabular-nums ${totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {totalPnl >= 0 ? '+' : ''}{fmtTotal(totalPnl)}
                  </p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-0.5">Return</p>
                  <p className={`text-sm font-semibold tabular-nums ${totalPnlPct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {totalPnlPct >= 0 ? '+' : ''}{totalPnlPct.toFixed(1)}%
                  </p>
                </div>
              </div>
            )}
          </div>
          {showProceedsNote && (
            <p className="text-[11px] mt-2" style={{ color: 'var(--color-sub)' }}>
              You sized your exit at ≈{fmtTotal(sellValueDisplay)} (current value); your tiers target{' '}
              {proceedsDiff > 0 ? 'higher' : 'lower'} prices, so expected proceeds are {proceedsDiff > 0 ? 'higher' : 'lower'}.
            </p>
          )}
        </div>
      )}

      {/* ── Save plan — name + button on one row ────────────────────────────── */}
      <div className="pt-1">
        {editingPlanId && (
          <p className="mb-2 text-xs font-medium" style={{ color: '#b89968' }}>
            Editing: {editingLabel}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            type="text"
            value={planName}
            onChange={e => setPlanName(e.target.value)}
            placeholder="Plan name (optional)"
            className={inputClass + ' sm:flex-1'}
          />
          <div className="flex items-center gap-3 sm:ml-auto">
            {editingPlanId && (
              <button
                type="button"
                onClick={resetPlanner}
                className="text-xs whitespace-nowrap transition-colors"
                style={{ color: 'var(--color-sub)' }}
                onMouseEnter={e => (e.currentTarget.style.color = 'rgb(var(--sl-100))')}
                onMouseLeave={e => (e.currentTarget.style.color = 'var(--color-sub)')}
              >
                Cancel edit
              </button>
            )}
            <button
              type="button"
              onClick={handleSavePlan}
              disabled={!canSave}
              className="flex items-center justify-center gap-2 rounded-lg font-medium transition-colors whitespace-nowrap"
              style={{
                backgroundColor: '#d4b483',
                color: '#0c0e12',
                padding: '9px 16px',
                fontSize: 12,
                fontWeight: 500,
                opacity: canSave ? 1 : 0.4,
                cursor: canSave ? 'pointer' : 'not-allowed',
              }}
            >
              {editingPlanId ? 'Update plan' : 'Save plan'}
            </button>
          </div>
        </div>
        {!canSave && (
          <p className="mt-2 text-xs text-slate-500">
            Pick an asset, choose how much to sell, and make tiers total 100% to save this plan.
          </p>
        )}
      </div>
    </>
  );

  const toast = savedToast && (
    <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-800 border border-slate-700 shadow-xl shadow-black/30">
      <span className="flex items-center justify-center w-5 h-5 rounded-full bg-emerald-500/15 flex-shrink-0">
        <Check className="w-3.5 h-3.5 text-emerald-400" />
      </span>
      <p className="text-sm text-slate-100 font-medium">{savedToastMsg}</p>
    </div>
  );

  if (embedded) {
    return <div ref={rootRef} className="space-y-4">{inner}{toast}</div>;
  }

  return (
    <div ref={rootRef} className="rounded-2xl bg-slate-800/40 border border-slate-700 p-5 space-y-4">
      <CardHeader
        title="Split Sell Planner"
        subtitle="Plan staged exits with profit targets at each level"
      />
      {inner}
      {toast}
    </div>
  );
}
